import React from 'react';
import { X, FileText, AlertTriangle, Shield, Sparkles, DollarSign, Scale, Info } from 'lucide-react';

const TermsAndConditions = ({ onClose, embedded = false, standalone = false }) => {
  const Container = embedded ? 'div' : 'div';
  const contentClass = standalone
    ? ''
    : embedded
    ? 'p-6'
    : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';

  return (
    <div className={contentClass}>
      <div className={`bg-white rounded-2xl max-w-4xl w-full ${standalone ? '' : embedded ? '' : 'max-h-[90vh] overflow-hidden flex flex-col shadow-2xl'}`}>
        {/* Header */}
        {!embedded && !standalone && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
                <p className="text-sm text-gray-600">Last updated: May 6, 2026</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Standalone Header */}
        {standalone && (
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Terms and Conditions</h1>
                <p className="text-sm text-gray-600 mt-1">Last updated: May 6, 2026</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`${standalone ? '' : embedded ? '' : 'overflow-y-auto flex-1'} p-6`}>
          {/* Quick Summary Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-6">
            <h3 className="font-bold text-gray-900 mb-3 text-lg">Quick Summary</h3>
            <div className="space-y-2 text-sm">
              <p><strong className="text-gray-900">What PlanLens Does:</strong> Educational financial planning tool with portfolio tracking, retirement planning, and AI-powered analysis.</p>
              <p><strong className="text-gray-900">What PlanLens Does NOT Do:</strong> We do NOT provide financial advice, investment recommendations, or tell you what to buy or sell.</p>
            </div>
            <div className="mt-3 p-3 bg-white rounded border border-blue-200">
              <p className="font-semibold text-blue-900 mb-2">Key Points:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✅ Educational tool only</li>
                <li>✅ You make all your own decisions</li>
                <li>✅ AI provides insights, not advice</li>
                <li>✅ No guarantees on accuracy</li>
                <li>✅ Consult professionals before investing</li>
              </ul>
            </div>
          </div>

          <div className="prose prose-sm max-w-none space-y-6 text-gray-700">
            {/* Section 1 - Agreement to Terms */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Agreement to Terms</h3>
              <p>
                By using PlanLens (the "App", "Service", "we", "us", or "our"), you agree to these Terms and Conditions.
                If you don't agree, please don't use PlanLens.
              </p>

              <div className="bg-gray-50 p-4 rounded-lg mt-3">
                <p className="font-semibold text-gray-900 mb-2">Who We Are:</p>
                <ul className="list-none space-y-1 text-sm">
                  <li>PlanLens is operated as the PlanLens service</li>
                  <li><strong>Contact:</strong> support@planlens.ai</li>
                  <li><strong>Website:</strong> https://www.planlens.ai</li>
                </ul>
              </div>

              <p className="mt-3">
                <strong>Changes to Terms:</strong> We may update these terms occasionally. We'll notify you of
                significant changes via email or in-app notification. Continued use means you accept the new terms.
              </p>
            </section>

            {/* Section 2 - What PlanLens Is (And Isn't) */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. What PlanLens Is (And Isn't)</h3>

              <p className="font-semibold text-gray-900">2.1 What We Provide</p>
              <p className="mb-2">PlanLens is an <strong>educational software tool</strong> that offers:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li>Portfolio Tracking (stocks, bonds, real estate, commodities, cryptocurrency, cash)</li>
                <li>Enhanced Retirement Planning with AI-powered insights</li>
                <li>Net Worth Tracking with real-time calculations</li>
                <li>Liabilities Management (mortgages, loans, credit cards)</li>
                <li>Monte Carlo Simulations on retirement outcomes</li>
                <li>AI Analysis for portfolio allocation insights</li>
                <li>Investment Calculators</li>
                <li>Country-Specific Features (UK: ISA, SIPP, LISA; US: 401k, IRA, Roth IRA)</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">2.2 What We Are NOT</p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mb-3">
                <p className="font-semibold text-red-900 mb-2">❌ We Are NOT:</p>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>A registered investment advisor (SEC, FINRA, or FCA)</li>
                  <li>A financial planner or wealth manager</li>
                  <li>A broker-dealer or investment platform</li>
                  <li>Providing personalized financial advice</li>
                  <li>Recommending specific investments</li>
                  <li>Managing your money</li>
                </ul>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg mb-3">
                <p className="font-semibold text-amber-900 mb-2">🚫 Critical Disclaimer:</p>
                <p className="text-sm text-amber-800 mb-2">
                  <strong>United Kingdom Users:</strong> PlanLens is NOT authorised or regulated by the
                  Financial Conduct Authority (FCA). We do NOT provide regulated financial advice.
                </p>
                <p className="text-sm text-amber-800">
                  <strong>United States Users:</strong> PlanLens is NOT registered with the Securities and
                  Exchange Commission (SEC) or FINRA. We do NOT provide investment advice subject to federal
                  or state securities regulations.
                </p>
              </div>

              <p className="font-semibold text-gray-900 mt-4">2.3 You Must Consult Professionals</p>
              <p className="text-sm mb-2"><strong>BEFORE making any financial decisions, consult with:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li><strong>Financial Advisor:</strong> Licensed/FCA-authorised for investment advice</li>
                <li><strong>Tax Professional:</strong> Accountant or tax advisor for tax implications</li>
                <li><strong>Legal Advisor:</strong> Solicitor/attorney for legal matters</li>
                <li><strong>Pension Specialist:</strong> For retirement account decisions</li>
              </ul>
            </section>

            {/* Section 3 - AI-Powered Features */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. AI-Powered Features</h3>

              <div className="flex items-start space-x-3 bg-purple-50 p-4 rounded-lg mb-4">
                <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-900 mb-1">3.1 How Our AI Works</p>
                  <p className="text-sm text-purple-800">
                    PlanLens uses artificial intelligence to analyze portfolio allocation, suggest diversification
                    areas (educational only), generate insights, and answer questions about your financial plan.
                  </p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">3.2 AI Limitations and Disclaimers</p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg mb-3">
                <p className="font-semibold text-amber-900 mb-2">Important: Our AI is NOT a financial advisor.</p>
                <p className="text-sm text-amber-800 mb-2">You understand and agree that:</p>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>1. AI is educational only, NOT financial advice</li>
                  <li>2. AI can make mistakes (inaccurate, incomplete, or misleading information)</li>
                  <li>3. No personalization - AI doesn't understand your specific circumstances</li>
                  <li>4. Generic algorithms, not customized advice</li>
                  <li>5. Not regulatory compliant under FCA or SEC rules</li>
                  <li>6. No guarantees on accuracy or reliability</li>
                  <li>7. You are solely responsible for acting on (or ignoring) AI insights</li>
                </ul>
              </div>

              <p className="font-semibold text-gray-900 mt-4">3.3 AI Data Usage</p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg mb-3">
                <p className="font-semibold text-green-900 mb-1">What We DON'T Do:</p>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>❌ Sell your data to third parties</li>
                  <li>❌ Use your data to train third-party AI models</li>
                  <li>❌ Share personally identifiable information</li>
                  <li>❌ Train AI on your private financial details without anonymization</li>
                </ul>
              </div>
              <p className="text-sm">
                All data is encrypted and handled according to our Privacy Policy and GDPR requirements.
              </p>
            </section>

            {/* Section 4 - Your Responsibilities */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">4. Your Responsibilities</h3>

              <p className="font-semibold text-gray-900">4.1 Investment Decisions</p>
              <p className="mb-2">You acknowledge and agree:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li>✓ <strong>You</strong> make all investment decisions, not PlanLens</li>
                <li>✓ <strong>You</strong> are responsible for all financial outcomes, gains or losses</li>
                <li>✓ All investments carry risk, including total loss of principal</li>
                <li>✓ Past performance does NOT guarantee future results</li>
                <li>✓ Projections are hypothetical and may not occur</li>
                <li>✓ You should never invest more than you can afford to lose</li>
                <li>✓ Cryptocurrency and other investments are highly volatile</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">4.2 Accurate Information</p>
              <p className="text-sm mb-2">You must provide accurate and complete data. We are not responsible for
              errors in calculations based on your inaccurate inputs.</p>

              <p className="font-semibold text-gray-900 mt-4">4.3 Account Security</p>
              <p className="text-sm mb-2">You are responsible for keeping your password secure, not sharing your
              account, and notifying us immediately of unauthorized access.</p>

              <p className="font-semibold text-gray-900 mt-4">4.4 Prohibited Uses</p>
              <p className="text-sm mb-2">You may NOT:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Use PlanLens to provide financial advice to others</li>
                <li>Claim AI outputs are professional financial advice</li>
                <li>Violate any laws or regulations</li>
                <li>Attempt to hack or disrupt the Service</li>
                <li>Scrape or copy our data/algorithms</li>
                <li>Use the Service commercially without permission</li>
              </ul>
            </section>

            {/* Section 5 - Subscriptions and Payments */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">5. Subscriptions and Payments</h3>

              <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg mb-4">
                <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-900 mb-1">5.1 Plans and Pricing</p>
                  <p className="text-blue-800"><strong>Free Plan:</strong> Basic portfolio tracking</p>
                  <p className="text-blue-800"><strong>Premium:</strong> £4.99/month or £49.99/year (US: $5.99/$65)</p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">5.2 Billing via Stripe</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li>All payments processed by Stripe</li>
                <li>We do NOT store your credit card details</li>
                <li>Subscriptions auto-renew monthly or annually</li>
                <li>Cancel anytime before next billing date</li>
              </ul>

              <p className="font-semibold text-gray-900">5.3 Cancellation and Refunds</p>
              <p className="text-sm mb-2">
                Cancel anytime through Account Settings. Access continues until end of current billing period.
                Generally, no refunds for unused time.
              </p>
              <p className="text-sm mb-2">
                <strong>UK Consumer Rights:</strong> UK consumers have a 14-day cooling-off period.
                Contact us within 14 days to exercise your right to cancel.
              </p>
            </section>

            {/* Section 6 - Disclaimers and Limitations */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">6. Disclaimers and Limitations</h3>

              <p className="font-semibold text-gray-900">6.1 "As Is" Service</p>
              <p className="text-sm mb-2">
                PlanLens is provided "AS IS" and "AS AVAILABLE". We do NOT guarantee uninterrupted access,
                error-free operation, accurate calculations, or that the Service meets your specific needs.
              </p>

              <p className="font-semibold text-gray-900 mt-4">6.2 No Warranties</p>
              <p className="text-sm mb-2">
                TO THE MAXIMUM EXTENT PERMITTED BY UK LAW: We disclaim all warranties, express or implied,
                including merchantability, fitness for particular purpose, non-infringement, accuracy, and reliability.
              </p>
              <p className="text-sm italic">This doesn't affect your statutory consumer rights under UK law.</p>

              <p className="font-semibold text-gray-900 mt-4">6.3 Third-Party Data</p>
              <p className="text-sm">
                Market data from third-party sources may be delayed or inaccurate. We're not liable for
                third-party data errors. Always verify important data from official sources.
              </p>
            </section>

            {/* Section 7 - Limitation of Liability */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">7. Limitation of Liability</h3>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
                <p className="font-bold text-red-900 mb-2">IMPORTANT - PLEASE READ CAREFULLY:</p>
                <p className="text-sm text-red-800 mb-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY UK AND US LAW:
                </p>
                <p className="text-sm text-red-800 mb-2">
                  PlanLens SHALL NOT BE LIABLE for:
                </p>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>❌ Investment losses or gains</li>
                  <li>❌ Lost profits or revenue</li>
                  <li>❌ Missed investment opportunities</li>
                  <li>❌ Data loss or corruption</li>
                  <li>❌ Service interruptions</li>
                  <li>❌ Calculation errors or bugs</li>
                  <li>❌ Security breaches (despite our best efforts)</li>
                  <li>❌ Inaccurate AI analysis</li>
                  <li>❌ Reliance on AI outputs</li>
                  <li>❌ Any indirect, incidental, or consequential damages</li>
                </ul>
              </div>

              <p className="font-semibold text-gray-900">7.2 Maximum Liability Cap</p>
              <div className="bg-amber-50 p-3 rounded-lg mb-3">
                <p className="text-sm text-amber-900">
                  <strong>IF we are found liable, our total liability is limited to:</strong>
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  The lesser of: <strong>£100 (GBP) / $100 (USD)</strong> OR total amounts you paid us in the last 12 months.
                </p>
              </div>

              <p className="font-semibold text-gray-900">7.3 Why These Limitations Exist</p>
              <p className="text-sm">
                We're providing an affordable educational tool (£4.99/month), not professional financial advice
                (which costs £100-500/hour). The low subscription cost reflects this limited liability model.
              </p>
            </section>

            {/* Section 8 - Your Use of PlanLens */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">8. Your Use of PlanLens</h3>

              <p className="font-semibold text-gray-900">8.1 License Grant</p>
              <p className="text-sm mb-2">
                We grant you a limited, non-exclusive, non-transferable, revocable license to access and use
                PlanLens for personal, non-commercial use.
              </p>

              <p className="font-semibold text-gray-900 mt-4">8.2 Restrictions</p>
              <p className="text-sm mb-2">You may NOT:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Share your account with others</li>
                <li>Use PlanLens for commercial purposes</li>
                <li>Copy, modify, or reverse engineer our code</li>
                <li>Remove copyright notices or branding</li>
                <li>Use automated tools to scrape data</li>
                <li>Bypass payment systems or security measures</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">8.3 Intellectual Property</p>
              <p className="text-sm mb-2">
                <strong>We Own:</strong> "PlanLens" name, logo, trademarks, software code, algorithms, AI models, design, and all intellectual property rights.
              </p>
              <p className="text-sm">
                <strong>You Own:</strong> Your personal financial data and inputs.
              </p>
            </section>

            {/* Section 9 - Privacy and Data Protection */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">9. Privacy and Data Protection</h3>

              <p className="font-semibold text-gray-900">9.1 Privacy Policy</p>
              <p className="text-sm mb-3">
                Your use is also governed by our Privacy Policy at planlens.ai. Please read it carefully.
              </p>

              <p className="font-semibold text-gray-900">9.2 GDPR Compliance (UK & EU Users)</p>
              <p className="text-sm mb-2">Your Rights Under UK GDPR:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li>✓ Access your personal data (get a copy)</li>
                <li>✓ Correct inaccurate or incomplete data</li>
                <li>✓ Delete your data ("right to be forgotten")</li>
                <li>✓ Export your data (data portability)</li>
                <li>✓ Restrict processing or object to processing</li>
                <li>✓ Withdraw consent at any time</li>
              </ul>
              <p className="text-sm">
                <strong>To exercise rights:</strong> Email support@planlens.ai (we'll respond within 30 days)
              </p>

              <p className="font-semibold text-gray-900 mt-4">9.3 Data Security</p>
              <p className="text-sm mb-2">We implement:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Encryption in transit (HTTPS/TLS) and at rest (database encryption)</li>
                <li>Secure authentication (password hashing, Google-based Auth)</li>
                <li>Regular security updates and monitoring</li>
                <li>Access controls and role-based permissions</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">9.4 Data Retention</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Active accounts: Data retained while account active</li>
                <li>Canceled subscriptions: Data retained 30 days, then deleted</li>
                <li>Deleted accounts: Data deleted within 30 days</li>
                <li>Billing records: Retained 7 years (UK tax law)</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">9.5 International Data Transfers</p>
              <p className="text-sm">
                Your data may be stored on UK, EU, or US servers. We use Firebase (Google Cloud Platform)
                with Standard Contractual Clauses (SCCs) for UK/EU → US transfers.
              </p>
            </section>

            {/* Section 10 - Termination */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">10. Termination</h3>

              <p className="font-semibold text-gray-900">10.1 You Can Terminate</p>
              <p className="text-sm mb-2">
                Cancel subscription through Account Settings or email support@planlens.ai. For account deletion,
                email "Delete My Account" - we'll delete your data within 30 days (cannot be undone).
              </p>

              <p className="font-semibold text-gray-900 mt-4">10.2 We Can Terminate</p>
              <p className="text-sm mb-2">
                We may suspend or terminate your access if you violate these Terms, use the Service fraudulently,
                engage in prohibited activities, or if required by law.
              </p>

              <p className="font-semibold text-gray-900 mt-4">10.3 Effect of Termination</p>
              <p className="text-sm">
                Your license ends immediately, outstanding fees remain due, and your data may be deleted after 30 days.
              </p>
            </section>

            {/* Section 11 - Governing Law and Disputes */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">11. Governing Law and Disputes</h3>

              <div className="flex items-start space-x-3 bg-indigo-50 p-4 rounded-lg mb-4">
                <Scale className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-indigo-900 mb-1">11.1 Governing Law</p>
                  <p className="text-sm text-indigo-800">
                    These Terms are governed by the laws of <strong>England and Wales</strong>.
                  </p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">11.2 Jurisdiction</p>
              <div className="bg-amber-50 p-3 rounded-lg mb-3">
                <p className="text-sm text-amber-900">
                  <strong>For All Users (UK, US, and Worldwide):</strong>
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  Any disputes shall be subject to the <strong>exclusive jurisdiction of the courts of England and Wales</strong>.
                  Legal disputes must be brought in UK courts (not US courts, even for US users).
                </p>
              </div>

              <p className="font-semibold text-gray-900">11.3 Dispute Resolution</p>
              <p className="text-sm">
                If you have a complaint, email support@planlens.ai. We'll respond within 30 days. Most issues
                can be resolved amicably.
              </p>

              <p className="font-semibold text-gray-900 mt-4">11.4 Consumer Rights</p>
              <p className="text-sm">
                <strong>UK Consumers:</strong> Nothing affects your statutory rights under UK consumer protection law
                (Consumer Rights Act 2015).
              </p>
            </section>

            {/* Section 12 - Regulatory Compliance */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">12. Regulatory Compliance</h3>

              <p className="font-semibold text-gray-900">12.1 United Kingdom</p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mb-3">
                <p className="text-sm text-red-900">
                  <strong>Financial Conduct Authority (FCA):</strong> PlanLens is NOT authorised or
                  regulated by the FCA. We do NOT provide investment advice, manage client assets, or conduct
                  regulated activities under FSMA 2000.
                </p>
                <p className="text-sm text-red-900 mt-2">
                  You do NOT have access to Financial Ombudsman Service (FOS) or Financial Services
                  Compensation Scheme (FSCS) protection.
                </p>
              </div>

              <p className="font-semibold text-gray-900">12.2 United States</p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mb-3">
                <p className="text-sm text-red-900">
                  <strong>SEC & FINRA:</strong> PlanLens is NOT registered as an investment advisor with
                  the SEC or any state regulator. NOT a FINRA-registered broker-dealer.
                </p>
                <p className="text-sm text-red-900 mt-2">
                  You do NOT have SEC, FINRA oversight, or SIPC coverage.
                </p>
              </div>

              <p className="font-semibold text-gray-900">12.3 Know Your Customer (KYC)</p>
              <p className="text-sm">
                We do NOT conduct KYC/identity verification because we don't hold or manage your money,
                execute trades, or provide regulated services.
              </p>
            </section>

            {/* Section 13 - Miscellaneous */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">13. Miscellaneous</h3>

              <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                <li><strong>Entire Agreement:</strong> These Terms, Privacy Policy, and Cookie Policy constitute the entire agreement.</li>
                <li><strong>Severability:</strong> If any provision is invalid, remaining provisions remain in effect.</li>
                <li><strong>No Waiver:</strong> Our failure to enforce doesn't constitute waiver.</li>
                <li><strong>Assignment:</strong> You cannot transfer these Terms. We may assign to another company.</li>
                <li><strong>Force Majeure:</strong> Not liable for delays due to events beyond our control.</li>
                <li><strong>Language:</strong> English version controls in case of conflicts.</li>
              </ul>

              <p className="text-sm mt-3">
                <strong>Updates:</strong> Significant changes get 30 days advance notice. Minor changes effective immediately.
              </p>
            </section>

            {/* Section 14 - Indemnification */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">14. Indemnification</h3>
              <p className="text-sm mb-2">
                You agree to indemnify, defend, and hold harmless PlanLens from any claims, damages, losses,
                and expenses arising from:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Your use or misuse of PlanLens</li>
                <li>Your violation of these Terms</li>
                <li>Any investment or financial decisions you make</li>
                <li>Your reliance on AI-generated content</li>
                <li>Claims that you provided financial advice to others using PlanLens</li>
              </ul>
            </section>

            {/* Section 15 - Contact Information */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">15. Contact Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">PlanLens</p>
                <ul className="list-none space-y-1 text-sm">
                  <li><strong>Contact:</strong> support@planlens.ai</li>
                  <li><strong>Website:</strong> https://www.planlens.ai</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <p className="text-sm"><strong>Expected Response Times:</strong></p>
                  <p className="text-sm">Free Plan: Within 48 hours (business days)</p>
                  <p className="text-sm">Premium Plan: Within 24 hours (business days)</p>
                </div>
              </div>
            </section>

            {/* Section 16 - Special Provisions */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">16. Special Provisions for Different Users</h3>

              <p className="font-semibold text-gray-900">16.1 UK Users - Consumer Contracts Regulations</p>
              <p className="text-sm mb-2">
                <strong>14-Day Cooling-Off Period:</strong> UK consumers have the right to cancel within 14 days.
                By using the Service immediately, you request that we begin performance and acknowledge you may
                lose your right to a full refund.
              </p>

              <p className="font-semibold text-gray-900 mt-4">16.2 California Users - CCPA Rights</p>
              <p className="text-sm mb-2">California residents have additional rights:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2 text-sm">
                <li>Right to Know what personal information we collect</li>
                <li>Right to Delete your personal information</li>
                <li>Right to Opt-Out (we don't sell data)</li>
                <li>Right to Non-Discrimination</li>
              </ul>
              <p className="text-sm">Email support@planlens.ai with "California Privacy Request"</p>

              <p className="font-semibold text-gray-900 mt-4">16.3 Users Outside UK/US</p>
              <p className="text-sm">
                These Terms still apply. You're responsible for compliance with your local laws.
                If use of PlanLens is prohibited in your jurisdiction, do not use the Service.
              </p>
            </section>

            {/* Final Warnings */}
            <section className="bg-gradient-to-r from-red-50 to-orange-50 p-5 rounded-lg border-2 border-red-300">
              <h3 className="font-bold text-gray-900 mb-3 text-lg">⚠️ IMPORTANT FINAL WARNINGS</h3>
              <div className="space-y-2 text-sm text-gray-800">
                <p><strong>1. PlanLens is NOT a Financial Advisor</strong> - We provide calculators and educational tools, NOT investment advice.</p>
                <p><strong>2. All Investments Carry Serious Risk</strong> - You can lose some or ALL of your money.</p>
                <p><strong>3. Consult Real Professionals</strong> - Talk to FCA-authorised (UK) or SEC-registered (US) advisors.</p>
                <p><strong>4. Verify Everything</strong> - Don't rely solely on PlanLens calculations.</p>
                <p><strong>5. You're in Complete Control</strong> - You make 100% of decisions and accept 100% of risks.</p>
                <p><strong>6. No Guarantees</strong> - We don't guarantee accuracy, returns, or data security.</p>
                <p><strong>7. AI Can Be Wrong</strong> - AI may "hallucinate" or generate incorrect information.</p>
                <p><strong>8. Liability Capped at £100/$100</strong> - We operate on thin margins with affordable pricing.</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-blue-50 p-5 rounded-lg border-2 border-blue-300">
              <p className="font-bold text-blue-900 mb-3 text-lg">ACKNOWLEDGMENT AND ACCEPTANCE</p>
              <p className="text-sm text-blue-800 mb-3">
                BY CLICKING "I AGREE", CREATING AN ACCOUNT, OR USING PLANLENS, YOU EXPLICITLY ACKNOWLEDGE:
              </p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ You are at least 18 years old</li>
                <li>✓ You have read and understood these Terms</li>
                <li>✓ PlanLens is for educational purposes ONLY</li>
                <li>✓ You will NOT rely on PlanLens as financial advice</li>
                <li>✓ You will consult licensed professionals before investing</li>
                <li>✓ You accept ALL risks associated with using PlanLens</li>
                <li>✓ AI can make mistakes and is not a substitute for human advisors</li>
                <li>✓ You're solely responsible for your financial decisions</li>
                <li>✓ PlanLens has NO liability for your investment outcomes</li>
                <li>✓ UK law and UK courts govern these Terms</li>
                <li>✓ Our liability is capped at £100/$100 maximum</li>
                <li>✓ You're using PlanLens voluntarily and at your own risk</li>
              </ul>
              <p className="text-sm text-blue-900 font-bold mt-3">
                IF YOU DO NOT AGREE, DO NOT USE PLANLENS.
              </p>
            </section>

            {/* Summary */}
            <section className="bg-gradient-to-r from-green-50 to-blue-50 p-5 rounded-lg border-2 border-green-300">
              <p className="font-bold text-gray-900 mb-3 text-lg">Summary of Key Points</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-800">
                <div>
                  <p className="font-semibold">PlanLens Is:</p>
                  <ul className="list-none space-y-1 ml-2">
                    <li>✅ Educational planning calculator</li>
                    <li>✅ Portfolio tracking tool</li>
                    <li>✅ AI-powered analysis platform</li>
                    <li>✅ Affordable (£4.99/month)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold">PlanLens Is NOT:</p>
                  <ul className="list-none space-y-1 ml-2">
                    <li>❌ Licensed financial advisor</li>
                    <li>❌ Providing investment advice</li>
                    <li>❌ Managing your money</li>
                    <li>❌ Guaranteeing accuracy</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-sm"><strong>Maximum Liability:</strong> £100 / $100</p>
                <p className="text-sm"><strong>Governing Law:</strong> England and Wales (UK)</p>
                <p className="text-sm"><strong>Questions?</strong> support@planlens.ai</p>
              </div>
            </section>

            {/* Version Info */}
            <section className="text-center text-sm text-gray-600 pt-4 border-t border-gray-200">
              <p><strong>Version:</strong> 1.0</p>
              <p><strong>Effective Date:</strong> May 6, 2026</p>
              <p><strong>Last Updated:</strong> May 6, 2026</p>
              <p className="mt-2 italic">© 2025 PlanLens. All rights reserved.</p>
            </section>
          </div>
        </div>

        {/* Footer */}
        {!embedded && onClose && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsAndConditions;
